import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import { BottomSheet, Box, Button, Column, CustomModal, Divider, Row, Typography, useTheme } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { isValidHexColor } from '~/business-logic/colorsLogic';
import { getDisabledTextColorForBackground, getTextColorsForBackground } from '~/business-logic/contrastLogic';
import { useResponsive } from '~/client-flow/contexts';
import { buildSystemVariableResolution, templateVariablesTextsPath } from '~/components/AgreementStudioPage/TemplateVariables/TemplateVariables.utils';
import { VariableInlineRenderer } from '~/components/AgreementStudioPage/TemplateVariables/VariableInlineRenderer/VariableInlineRenderer';
import { useLocaleKeys } from '~/i18n';
import { AgreementDataModel, relationshipModel } from '~/models';
import { toDate } from '~/models/time/translator';
import { COB_MODAL_ROOT_ID } from '../../../OnboardingPage';
import styles from './PackageDetailsModal.module.scss';
import { PackageDetailsService } from './PackageDetailsService';
export const GetPackageDetailsModalTID = (parentTestId)=>getTestIds({
        parentTestId
    });
const buildContrastStyle = (bgStandardHex)=>{
    if (!bgStandardHex || !isValidHexColor(bgStandardHex)) return {};
    const text = getTextColorsForBackground(bgStandardHex);
    return {
        '--text-on-standard-bg': text.primary,
        '--text-on-standard-bg-secondary': text.secondary,
        '--text-on-standard-bg-disabled': getDisabledTextColorForBackground(bgStandardHex),
        '--text-standard': text.primary,
        '--text-secondary': text.secondary
    };
};
const PackageDetailsContent = (param)=>{
    let { packages, initialIndex, relationship, now, onClose, testIds, isMobile } = param;
    var _focusedPackage_services;
    const { t: packageDetailsModalTexts } = useLocaleKeys('clientFlow.packageDetailsModal');
    const { t: templateVarTexts } = useLocaleKeys(templateVariablesTextsPath);
    const theme = useTheme();
    const bgStandard = theme['--bg-standard'];
    const contrastStyle = useMemo(()=>buildContrastStyle(bgStandard), [
        bgStandard
    ]);
    const total = packages.length;
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [direction, setDirection] = useState(null);
    const agreementData = useMemo(()=>AgreementDataModel.fromRelationship(relationship), [
        relationship
    ]);
    const resolvedValues = useMemo(()=>{
        var _relationship_buyer_firstName, _relationship_buyer_lastName;
        return buildSystemVariableResolution({
            clientCompanyName: relationshipModel.customerName(relationship),
            clientFirstName: (_relationship_buyer_firstName = relationship.buyer.firstName) !== null && _relationship_buyer_firstName !== void 0 ? _relationship_buyer_firstName : undefined,
            clientLastName: (_relationship_buyer_lastName = relationship.buyer.lastName) !== null && _relationship_buyer_lastName !== void 0 ? _relationship_buyer_lastName : undefined,
            clientEmail: relationship.buyer.email,
            vendorBusinessName: relationship.vendor.name,
            agreementName: relationship.title,
            effectiveDate: agreementData.effectiveDate ? toDate(agreementData.effectiveDate) : null,
            currentDate: now
        }, templateVarTexts);
    }, [
        relationship,
        agreementData.effectiveDate,
        now
    ]);
    useEffect(()=>{
        setCurrentIndex(initialIndex);
        setDirection(null);
    }, [
        initialIndex
    ]);
    const focusedPackage = packages[currentIndex];
    const canNavigate = total > 1;
    const goPrevious = useCallback(()=>{
        if (!canNavigate) return;
        setDirection('back');
        setCurrentIndex((prev)=>(prev - 1 + total) % total);
    }, [
        canNavigate,
        total
    ]);
    const goNext = useCallback(()=>{
        if (!canNavigate) return;
        setDirection('forward');
        setCurrentIndex((prev)=>(prev + 1) % total);
    }, [
        canNavigate,
        total
    ]);
    useEffect(()=>{
        const isEditableTarget = (el)=>{
            if (!el) return false;
            if (el instanceof HTMLElement && el.isContentEditable) return true;
            const tag = el.tagName;
            return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
        };
        const onKey = (e)=>{
            if (e.key === 'Escape') {
                onClose();
                return;
            }
            if (isEditableTarget(document.activeElement)) return;
            if (e.key === 'ArrowLeft') goPrevious();
            else if (e.key === 'ArrowRight') goNext();
        };
        window.addEventListener('keydown', onKey);
        return ()=>window.removeEventListener('keydown', onKey);
    }, [
        goPrevious,
        goNext,
        onClose
    ]);
    if (!focusedPackage) return null;
    const services = (_focusedPackage_services = focusedPackage.services) !== null && _focusedPackage_services !== void 0 ? _focusedPackage_services : [];
    const headerTitle = /*#__PURE__*/ _jsx(Typography, {
        variant: "h6",
        weight: "normal",
        testId: testIds.Title(),
        children: /*#__PURE__*/ _jsx(VariableInlineRenderer, {
            text: focusedPackage.name,
            resolvedValues: resolvedValues
        })
    });
    const body = /*#__PURE__*/ _jsx(Box, {
        className: cn(styles.slide, {
            [styles.slideForward]: direction === 'forward',
            [styles.slideBack]: direction === 'back'
        }),
        testId: testIds.Slide(currentIndex),
        children: /*#__PURE__*/ _jsx(Column, {
            gap: 24,
            className: styles.servicesList,
            testId: testIds.Body(),
            children: services.map((service, idx)=>/*#__PURE__*/ _jsxs(Fragment, {
                    children: [
                        idx > 0 && /*#__PURE__*/ _jsx(Box, {
                            className: styles.dividerWrap,
                            children: /*#__PURE__*/ _jsx(Divider, {
                                margin: 0
                            })
                        }),
                        /*#__PURE__*/ _jsx(PackageDetailsService, {
                            service: service,
                            relationship: relationship,
                            now: now,
                            agreementData: agreementData,
                            resolvedValues: resolvedValues,
                            testId: testIds.ServiceItem(idx)
                        })
                    ]
                }, service.id))
        })
    }, focusedPackage.id);
    const footer = /*#__PURE__*/ _jsxs(Row, {
        gap: 12,
        alignItems: "center",
        justifyContent: "center",
        className: isMobile ? styles.footerRowMobile : undefined,
        children: [
            /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'package_details_previous'
                },
                skin: "primaryOutline",
                size: isMobile ? 'small' : 'medium',
                fluid: isMobile,
                disabled: !canNavigate,
                className: cn(styles.outlineButton, {
                    [styles.footerButtonMobile]: isMobile
                }),
                onClick: goPrevious,
                testId: testIds.PreviousButton(),
                children: packageDetailsModalTexts.previousPackage()
            }),
            /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'package_details_next'
                },
                skin: "primary",
                size: isMobile ? 'small' : 'medium',
                fluid: isMobile,
                disabled: !canNavigate,
                className: cn(styles.primaryButton, {
                    [styles.footerButtonMobile]: isMobile
                }),
                onClick: goNext,
                testId: testIds.NextButton(),
                children: packageDetailsModalTexts.nextPackage()
            })
        ]
    });
    if (isMobile) {
        return /*#__PURE__*/ _jsxs("div", {
            className: styles.bottomSheetContent,
            style: contrastStyle,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: styles.bottomSheetTitle,
                    children: headerTitle
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: styles.bottomSheetBody,
                    children: body
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: styles.bottomSheetFooter,
                    children: footer
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx(CustomModal, {
        className: styles.modal,
        width: "100%",
        height: "100%",
        maxWidth: "none",
        maxHeight: "none",
        style: contrastStyle,
        onClose: onClose,
        header: /*#__PURE__*/ _jsx(Box, {
            className: styles.headerTitle,
            children: headerTitle
        }),
        footer: footer,
        contentUseFullWidth: true,
        testId: testIds.Root(),
        bi: {
            closeButton: {
                name: 'package_details_close'
            }
        },
        children: body
    });
};
export const PackageDetailsModal = (param)=>{
    let { open, packages, initialIndex, relationship, now, onClose, testId } = param;
    const { isMobile } = useResponsive();
    const { PackageDetailsModalTestIds: testIds } = useMemo(()=>GetPackageDetailsModalTID(testId), [
        testId
    ]);
    if (isMobile) {
        return /*#__PURE__*/ _jsx(BottomSheet, {
            open: open,
            onClose: onClose,
            variant: "branded",
            paddingBottom: 0,
            testId: testIds.Root(),
            children: open && /*#__PURE__*/ _jsx(PackageDetailsContent, {
                packages: packages,
                initialIndex: initialIndex,
                relationship: relationship,
                now: now,
                onClose: onClose,
                testIds: testIds,
                isMobile: true
            })
        });
    }
    if (!open) return null;
    const portalTarget = typeof document !== 'undefined' ? document.getElementById(COB_MODAL_ROOT_ID) : null;
    const overlay = /*#__PURE__*/ _jsxs(Box, {
        className: styles.overlay,
        children: [
            /*#__PURE__*/ _jsx(Box, {
                className: styles.backdrop,
                onClick: onClose,
                "aria-hidden": true,
                testId: testIds.Backdrop()
            }),
            /*#__PURE__*/ _jsx(Box, {
                className: styles.modalWrapper,
                children: /*#__PURE__*/ _jsx(PackageDetailsContent, {
                    packages: packages,
                    initialIndex: initialIndex,
                    relationship: relationship,
                    now: now,
                    onClose: onClose,
                    testIds: testIds,
                    isMobile: false
                })
            })
        ]
    });
    return portalTarget ? /*#__PURE__*/ createPortal(overlay, portalTarget) : overlay;
};
